import { useState, useEffect, useCallback } from "react";
import { FileText, Upload, Trash2, ExternalLink, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { uploadWOTechDoc, deleteWOTechDoc, getWOTechDocUrl, getWOTechInfo, getPartDocUrl } from "@/lib/api";

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function TechInfoSection({ woId }) {
  const [woDocsMeta, setWoDocsMeta] = useState([]);
  const [partDocs, setPartDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const loadDocs = useCallback(async () => {
    try {
      const res = await getWOTechInfo(woId);
      setWoDocsMeta(res.data.wo_docs || []);
      setPartDocs(res.data.part_docs || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [woId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await uploadWOTechDoc(woId, formData);
      toast.success(`Uploaded ${file.name}`);
      loadDocs();
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId, filename) => {
    try {
      await deleteWOTechDoc(woId, docId);
      toast.success(`Deleted ${filename}`);
      setWoDocsMeta(prev => prev.filter(d => d.id !== docId));
    } catch {
      toast.error("Failed to delete document");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const totalDocs = woDocsMeta.length + partDocs.length;

  return (
    <div className="space-y-2" data-testid="tech-info-section">
      <div className="flex items-center gap-2">
        <h4 className="font-semibold text-sm flex-1">Tech Info</h4>
        <span className="text-xs text-slate-500 num">{totalDocs} doc{totalDocs !== 1 ? "s" : ""}</span>
      </div>

        <div className="space-y-3">
          {loading ? (
            <div className="text-xs text-slate-400 text-center py-4">Loading tech documents...</div>
          ) : (
            <>
              {/* Auto-pulled from Parts */}
              {partDocs.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold text-slate-400 mb-1.5 flex items-center gap-1">
                    <Wrench className="w-3 h-3" /> From parts library
                  </div>
                  <div className="space-y-1">
                    {partDocs.map(doc => (
                      <div key={`part-${doc.id}`} className="flex items-center gap-2 p-2 border rounded bg-blue-50/30 hover:bg-blue-50 transition-colors" data-testid={`part-doc-${doc.id}`}>
                        <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-slate-700 truncate">{doc.filename}</div>
                          <div className="text-[10px] text-slate-400">
                            {doc.part_name}{doc.part_number ? ` [${doc.part_number}]` : ""} &middot; {formatSize(doc.size)}
                          </div>
                        </div>
                        <a href={getPartDocUrl(doc.part_id, doc.id)} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 gap-1" data-testid={`view-part-doc-${doc.id}`}>
                            <ExternalLink className="w-3 h-3" /> View
                          </Button>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* WO-specific docs */}
              {woDocsMeta.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold text-slate-400 mb-1.5">WO documents</div>
                  <div className="space-y-1">
                    {woDocsMeta.map(doc => (
                      <div key={`wo-${doc.id}`} className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50 transition-colors" data-testid={`wo-doc-${doc.id}`}>
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-slate-700 truncate">{doc.filename}</div>
                          <div className="text-[10px] text-slate-400">{formatSize(doc.size)}</div>
                        </div>
                        <a href={getWOTechDocUrl(woId, doc.id)} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 gap-1" data-testid={`view-wo-doc-${doc.id}`}>
                            <ExternalLink className="w-3 h-3" /> View
                          </Button>
                        </a>
                        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 text-red-400 hover:text-red-600" onClick={() => handleDelete(doc.id, doc.filename)} data-testid={`delete-wo-doc-${doc.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
                  isDragging ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-blue-400 hover:bg-blue-50/20"
                } ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`tech-doc-upload-${woId}`)?.click()}
                data-testid="tech-info-dropzone"
              >
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" className="hidden" id={`tech-doc-upload-${woId}`}
                  onChange={(e) => { if (e.target.files[0]) handleUpload(e.target.files[0]); e.target.value = ""; }} />
                {uploading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs text-blue-600">Uploading...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Upload className={`w-4 h-4 ${isDragging ? "text-blue-500" : "text-slate-400"}`} />
                    <span className="text-xs text-slate-500">{isDragging ? "Drop file here" : "Drop shop drawings, tech data, install instructions..."}</span>
                  </div>
                )}
              </div>

              {totalDocs === 0 && !uploading && (
                <div className="text-center py-2 text-xs text-slate-400">
                  No tech documents yet. Upload files or add Part Data in the equipment library.
                </div>
              )}
            </>
          )}
        </div>
    </div>
  );
}
